'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { Typography } from '@/components/ui/typography';
import { Card } from '@/components/ui/card';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { Shield, Bug, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

export function WhoWeAreSection() {
  const t = useTranslations('aboutPage.whoWeAre');
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  const features = [
    {
      icon: Shield,
      title: t('feature1.title'),
      description: t('feature1.description'),
    },
    {
      icon: Bug,
      title: t('feature2.title'),
      description: t('feature2.description'),
    },
    {
      icon: Zap,
      title: t('feature3.title'),
      description: t('feature3.description'),
    },
  ];

  return (
    <SectionWrapper
      ref={sectionRef.ref}
      variant="default"
      padding="md"
      className="relative overflow-hidden"
    >
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="container relative z-10 max-w-7xl mx-auto">
        <div className={cn(
          "mb-12 text-center transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        )}>
          <SectionHeading
            page="about"
            align="center"
            fallbackPill={t('pill')}
            fallbackTitle={t('title')}
            fallbackSubtitle={t('subtitle')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Card
                key={index}
                title={feature.title}
                description={feature.description}
                icon={IconComponent}
                index={index}
                showIndex={false}
                showFooterLink={false}
                animationDelay={200 + index * 100}
                titleVariant="h3"
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-center"
                renderContent={
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-5">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-500/10 dark:bg-primary-500/20 group-hover:bg-primary-500/20 dark:group-hover:bg-primary-500/30 group-hover:scale-110 transition-all duration-300">
                        <IconComponent className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                      </div>
                    </div>
                    <Typography variant="h3" className="mb-4 text-gray-900 dark:text-white">
                      {feature.title}
                    </Typography>
                    <Typography variant="p" className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {feature.description}
                    </Typography>
                  </div>
                }
              />
            );
          })}
        </div>
      </div>
    </SectionWrapper>
  );
}

